import moment from 'moment'
import { isEmpty } from 'lodash'

import { apiService } from '@/api'
import * as redux from '@/reducers/commandCenter/commandCenter.redux'
import * as actions from '@/reducers/commandCenter/commandCenter.actions'
import { ADD_MESSAGE_TO_CHAT } from '@/reducers/realtime/realtimeChats.redux'
import {
  WATCH_TRANSCRIPT,
  SET_WATCHED_AGENT,
  SET_WATCH_STATUS,
  CLEAR_AGENT_TRANSCRIPT,
  addTranscriptUtterance,
  endAgentTranscript,
} from '@/reducers/realtime/liveTranscripts.redux'
import { formatTranscriptUtterance } from '@/reducers/realtime/liveTranscripts.helpers'
import { LOGOUT_USER } from '@/reducers/auth/currentUser.redux'

export const ccSocketMiddleware = (worker, broadcast) => (params) => (next) => (action) => {
  const { dispatch, getState } = params
  const { type } = action
  const { username, token, organizationid } = getState().currentUser

  switch (type) {
    case redux.CONNECT:
      // Initialize classes for worker and broadcast
      // Only one instance will be created, if one already exists it will be skipped
      worker.init()
      broadcast.init()

      if (worker.unsupportedBrowser || broadcast.unsupportedBrowser) {
        return next(action)
      }

      // Set up event listeners to receive broadcasts
      broadcast.on('message', ({ data }) => {
        console.info('Receiving message from Command Center Broadcast', data)

        if (data.type === redux.SOCKET_OPEN) {
          dispatch({ type: redux.SOCKET_OPEN })
        }
        if (data.type === redux.SOCKET_CLOSED) {
          dispatch({ type: redux.SOCKET_CLOSED })
        }
        if (data.type === redux.RECEIVE_BROADCAST) {
          const { message } = data

          // Incoming chat from an agent — delivered over the CC socket for
          // managers who don't have an RTC socket connection. The server-side
          // dedupe ensures we never receive this on both sockets at once.
          if (message?.from_user_id && message?.to_user_id) {
            dispatch({
              type: ADD_MESSAGE_TO_CHAT,
              agentUsername: message.agent_username,
              message: message.message,
              isMessageFromManager: false,
              date: moment().format('LT'),
              chatId: message.chat_id,
            })
            return
          }

          // Live transcript for the agent this socket was asked to watch. The
          // RTC socket only carries agents the manager has RTA alerts for, so
          // Live Listen relies on this relay instead (QCER-1098).
          if (message?.event_type === 'TRANSCRIPT') {
            // The relay is refcounted across tabs and the broadcast reaches all
            // of them, so a tab that has closed this agent's transcript still
            // receives the frames. Ignore them: that tab shows nothing, its RTC
            // path is no longer suppressed, and storing both duplicated every
            // line in its own copy of the thread.
            if (getState().liveTranscripts?.watchedAgents?.includes(message.user)) {
              dispatch(
                addTranscriptUtterance(message.user, formatTranscriptUtterance(message.data))
              )
            }
            return
          }

          // The server answers every watch. A refusal has to surface, or the
          // panel just sits there empty with nothing to explain it.
          if (message?.event_type === 'TRANSCRIPT_WATCH') {
            dispatch({
              type: SET_WATCH_STATUS,
              agentUsername: message.user,
              status: message.data?.status,
              reason: message.data?.reason,
            })
            return
          }

          // A hung-up call keeps its transcript on screen, marked as finished;
          // a new call throws the previous one's words away so the two never
          // read as one conversation.
          if (message?.event_type === 'CALL_STOP') {
            dispatch(endAgentTranscript(message.user))
          } else if (message?.event_type === 'CALL_START') {
            dispatch({ type: CLEAR_AGENT_TRANSCRIPT, agentUsername: message.user })
          }

          // Otherwise it's a call lifecycle event. The server reads the live
          // half of the row once per boundary and sends it with the event, so
          // there is nothing to ask for.
          const callData = message?.data?.call_data

          if (!isEmpty(callData)) {
            dispatch(redux.patchAgentCallData(message.user, callData))
            return
          }

          // No `call_data` means an older rtc. Fall back to the round trip, but
          // only for a row somebody is looking at — this socket is subscribed
          // to the caller's whole team, and refreshing on every event turned
          // each session into one rtc request per agent per call boundary.
          const state = getState()
          const wanted =
            state.commandCenter.rosterInUse || Boolean(state.realtimeChats?.chats?.[message?.user])

          if (!wanted) {
            return
          }

          const agent = state.commandCenter.agents.find(
            (agent) => agent.agent_username === message?.user
          )

          if (agent) {
            dispatch(actions.fetchAgentWithCallStatus(agent.agent_id))
          }
        }
      })

      broadcast.on('error', (err) => {
        console.info('An error was encountered in the Command Center Broadcast')
        console.error(err)
      })

      // Post a message to the worker to open the initial websocket connection
      worker.post({
        token,
        username,
        organizationid,
        type: redux.CONNECT,
        url: `${apiService.rtc_websocket}/ws/command-center`,
      })
      break

    case redux.CHANGE_SUBSCRIPTION:
      worker.post({ type, payload: action.payload })
      break

    case WATCH_TRANSCRIPT:
      // `CommandCenterSocketProvider` initializes the worker for the whole
      // session, but a watch can still be dispatched before CONNECT has run or
      // in a browser with no SharedWorker. Only record the watch once it has
      // actually gone out — otherwise the RTC socket would step aside for a
      // relay that never runs.
      if (worker.sharedWorker) {
        worker.post({
          type: WATCH_TRANSCRIPT,
          agentUsername: action.agentUsername,
          watching: action.watching,
        })
        dispatch({
          type: SET_WATCHED_AGENT,
          agentUsername: action.agentUsername,
          watching: action.watching,
        })
      }
      break

    case redux.SEND_CHAT: {
      // Spread the action payload (minus the action `type`) onto chatData so
      // the worker can forward the same shape the rtc worker uses.
      const { type: _ignored, ...chatData } = action
      worker.post({ type: redux.SEND_CHAT, chatData })
      break
    }

    case redux.DISCONNECT:
    case LOGOUT_USER:
      // On disconnect, worker and broadcast will always exist so it's not necessary to check, but
      // on logout from no token or bad token, these will never have been initialized.
      if (worker.sharedWorker) {
        // A logged-out session must not keep a socket relaying customer speech,
        // whatever the port refcount says.
        worker.post({ type: redux.DISCONNECT, force: type === LOGOUT_USER })
      }

      if (broadcast.broadcastChannel) {
        broadcast.close()
      }

      // The worker's own socketClosed broadcast lands on a channel we just
      // closed, so nothing else would ever flip this back. Consumers read
      // `socketOpen` to decide whether the Live Listen roster and the
      // transcript relay are still live — a stuck `true` makes both lie.
      dispatch({ type: redux.SOCKET_CLOSED })

      break

    default:
      break
  }

  return next(action)
}
